import { useQuery } from '@tanstack/react-query';
import type { DiaryEntry } from '@nutrigeist/shared';
import { diaryApi } from '../api/endpoints.js';
import { ClockIcon } from './icons.js';
import { addDays, formatDuration, formatTime } from '../lib/format.js';

interface EatingWindowProps {
  date: string;
  timeZone: string;
  /** The viewed day's entries, already loaded by the page. */
  entries: DiaryEntry[];
}

/** Latest `loggedAt` among a set of entries, in epoch ms, or null if empty. */
function lastMealMs(entries: DiaryEntry[]): number | null {
  if (entries.length === 0) return null;
  return Math.max(...entries.map((entry) => Date.parse(entry.loggedAt)));
}

/**
 * The day's eating window — first to last intake — and the overnight fast that
 * led into it. Both are read straight from the diary timestamps already on
 * screen; the only extra data is the previous day's last entry, for the fast.
 */
export function EatingWindow({ date, timeZone, entries }: EatingWindowProps): JSX.Element | null {
  const previousDate = addDays(date, -1);

  // Shares the Today page's cache key, so navigating to yesterday is instant.
  const previousDay = useQuery({
    queryKey: ['diary', 'day', previousDate],
    queryFn: () => diaryApi.day({ date: previousDate, timezone: timeZone }),
    enabled: entries.length > 0,
  });

  if (entries.length === 0) return null;

  const times = entries.map((entry) => Date.parse(entry.loggedAt)).sort((a, b) => a - b);
  const first = times[0]!;
  const last = times[times.length - 1]!;
  const firstIso = new Date(first).toISOString();

  const previousEntries = previousDay.data
    ? previousDay.data.meals.flatMap((meal) => meal.entries)
    : [];
  const previousLast = lastMealMs(previousEntries);
  // Only sensible as an overnight fast: positive and under two days.
  const fastMs =
    previousLast != null && first - previousLast > 0 && first - previousLast < 48 * 3_600_000
      ? first - previousLast
      : null;

  return (
    <div className="card stack">
      <div className="row-between">
        <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ClockIcon />
          <span>Essensfenster</span>
        </div>
        {last > first && <span className="hint">{formatDuration(last - first)}</span>}
      </div>

      {last > first ? (
        <div>
          {formatTime(firstIso, timeZone)}–{formatTime(new Date(last).toISOString(), timeZone)}
        </div>
      ) : (
        <div>Erste Mahlzeit {formatTime(firstIso, timeZone)}</div>
      )}

      {fastMs != null && <div className="hint">Nachtfasten davor: {formatDuration(fastMs)}</div>}
    </div>
  );
}
